import type {
  LockerverseAuction as Auction,
  LockerverseAuctionItem as AuctionItem,
  LockerverseAuctionStatus,
} from "@lockerverse/sdk/auctions";
import { ChevronLeft } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { AuctionDescription } from "./auction-description.tsx";
import {
  formatAuctionBoundary,
  getAuctionDisplayStatus,
  getNextAuctionItem,
} from "./auction-display.ts";
import { LockerverseAuctionItemCard } from "./lockerverse-auction-item-card.tsx";
import type { LockerverseStyle, LockerverseTheme } from "./theme.ts";
import { useAuctionBoundary } from "./use-auction-boundary.ts";

export type LockerverseAuctionItemRenderContext = {
  auction: Auction;
  item: AuctionItem;
  /** Effective auction and item status, including elapsed closing dates. */
  status: LockerverseAuctionStatus;
  /** Calls the host selection callback when one is supplied. Use href for native navigation. */
  onSelect: () => void;
  href?: string;
  /** Parent theme and Lockerverse CSS custom properties for default-card reuse. */
  theme: LockerverseTheme;
  style?: LockerverseStyle;
};
export type LockerverseAuctionProps = {
  auction: Auction;
  onSelectItem?: (auction: Auction, item: AuctionItem) => void;
  /** Controlled item status filter. Omit to use the internal filter state. */
  filter?: LockerverseAuctionItemFilter;
  onFilterChange?: (filter: LockerverseAuctionItemFilter) => void;
  /** Native links preserve opening filters and items in another tab. */
  getFilterHref?: (filter: LockerverseAuctionItemFilter) => string;
  getItemHref?: (auction: Auction, item: AuctionItem) => string;
  /** Rotate catalog photos. Static images are the default. */
  autoRotateImages?: boolean;
  theme?: LockerverseTheme;
  style?: LockerverseStyle;
  className?: string;
  showFilters?: boolean;
  showHeader?: boolean;
  /** Show the optional catalog introduction and count. Hidden by default. */
  showLineup?: boolean;
  onBack?: () => void;
  /** Replace the card inside each list item. Keep onSelect connected to your item view. */
  renderItem?: (context: LockerverseAuctionItemRenderContext) => ReactNode;
};
const FILTERS = [
  { label: "All", value: "all" },
  { label: "Live", value: "active" },
  { label: "Upcoming", value: "draft" },
  { label: "Closed", value: "finished" },
] as const;
export type LockerverseAuctionItemFilter = (typeof FILTERS)[number]["value"];

/** Display one loaded auction. Fetching and item navigation stay with the host. */
export function LockerverseAuction({
  auction,
  onSelectItem,
  filter: controlledFilter,
  onFilterChange,
  getFilterHref,
  getItemHref,
  autoRotateImages = false,
  theme = "dark",
  style,
  className,
  showFilters = true,
  showHeader = true,
  showLineup = false,
  onBack,
  renderItem,
}: LockerverseAuctionProps) {
  return (
    <section
      aria-label={auction.name}
      className={[
        "lockerverse-auctions",
        "lockerverse-auctions--catalog",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-lockerverse-theme={theme}
      style={style}
    >
      <AuctionContent
        auction={auction}
        {...(onSelectItem ? { onSelectItem } : {})}
        {...(controlledFilter ? { filter: controlledFilter } : {})}
        {...(onFilterChange ? { onFilterChange } : {})}
        {...(getFilterHref ? { getFilterHref } : {})}
        {...(getItemHref ? { getItemHref } : {})}
        autoRotateImages={autoRotateImages}
        showFilters={showFilters}
        showHeader={showHeader}
        showLineup={showLineup}
        theme={theme}
        {...(style ? { style } : {})}
        {...(onBack ? { onBack } : {})}
        {...(renderItem ? { renderItem } : {})}
      />
    </section>
  );
}

/** Shared content without a second themed container when used in the browser. */
export function AuctionContent({
  auction,
  onSelectItem,
  filter: controlledFilter,
  onFilterChange,
  getFilterHref,
  getItemHref,
  autoRotateImages = false,
  theme = "dark",
  style,
  showFilters = true,
  showHeader = true,
  showLineup = false,
  onBack,
  renderItem,
}: LockerverseAuctionProps) {
  useAuctionBoundary(auction.startAt, auction.endAt, auction.items);
  const [filterState, setFilterState] = useState<{
    auctionId: string;
    value: LockerverseAuctionItemFilter;
  }>({ auctionId: auction.id, value: "all" });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const filter = showFilters
    ? (controlledFilter ??
      (filterState.auctionId === auction.id ? filterState.value : "all"))
    : "all";
  useEffect(() => {
    setFilterState({ auctionId: auction.id, value: "all" });
  }, [auction.id, showFilters]);
  const changeFilter = (value: LockerverseAuctionItemFilter) => {
    setFilterState({ auctionId: auction.id, value });
    onFilterChange?.(value);
  };
  const nextItem = getNextAuctionItem(auction);
  const focusHeading = Boolean(onBack);
  useEffect(() => {
    if (focusHeading) {
      (headingRef.current ?? backRef.current)?.focus();
    }
  }, [auction.id, focusHeading, showHeader]);
  const cardStyle = style
    ? Object.fromEntries(
        Object.entries(style).filter(([key]) =>
          key.startsWith("--lockerverse-")
        )
      )
    : undefined;
  const items = [...auction.items].sort(
    (left, right) =>
      left.position - right.position ||
      left.createdAt.localeCompare(right.createdAt)
  );
  const filtered = items.filter(
    (item) =>
      filter === "all" || getAuctionDisplayStatus(auction, item) === filter
  );
  return (
    <>
      {onBack ? (
        <button
          className="lockerverse-auctions__catalog-back"
          onClick={onBack}
          ref={backRef}
          type="button"
        >
          <ChevronLeft aria-hidden="true" size={16} />
          Auctions
        </button>
      ) : null}
      {showHeader ? (
        <header className="lockerverse-auctions__catalog-header">
          <h2 ref={headingRef} tabIndex={-1}>
            {auction.name}
          </h2>
          {auction.status !== "finished" && nextItem ? (
            <p className="lockerverse-auctions__catalog-next">
              Next opens {formatAuctionBoundary(auction, "draft", nextItem)}
            </p>
          ) : null}
          {auction.description ? (
            <AuctionDescription description={auction.description} />
          ) : null}
        </header>
      ) : null}
      {showLineup ? (
        <div className="lockerverse-auctions__catalog-lineup">
          <div>
            <h3>Auction lineup</h3>
            <p>Browse the current collection.</p>
          </div>
          <span>
            {filtered.length} {filtered.length === 1 ? "item" : "items"}
          </span>
        </div>
      ) : null}
      {showFilters ? (
        <fieldset
          aria-label="Filter auction items"
          className="lockerverse-auctions__catalog-filters"
        >
          {FILTERS.map((option) => {
            const href = getFilterHref?.(option.value);
            const label = (
              <>
                {option.label}
                <span>
                  {option.value === "all"
                    ? items.length
                    : items.filter(
                        (item) =>
                          getAuctionDisplayStatus(auction, item) ===
                          option.value
                      ).length}
                </span>
              </>
            );
            return href ? (
              <a
                aria-current={filter === option.value ? "page" : undefined}
                href={href}
                key={option.value}
                onClick={(event) => {
                  if (
                    onFilterChange &&
                    event.button === 0 &&
                    !event.metaKey &&
                    !event.ctrlKey &&
                    !event.shiftKey &&
                    !event.altKey
                  ) {
                    event.preventDefault();
                    changeFilter(option.value);
                  }
                }}
              >
                {label}
              </a>
            ) : (
              <button
                aria-pressed={filter === option.value}
                key={option.value}
                onClick={() => changeFilter(option.value)}
                type="button"
              >
                {label}
              </button>
            );
          })}
        </fieldset>
      ) : null}
      {filtered.length ? (
        <ul className="lockerverse-auctions__catalog-grid">
          {filtered.map((item) => {
            const context: LockerverseAuctionItemRenderContext = {
              auction,
              item,
              onSelect: () => onSelectItem?.(auction, item),
              status: getAuctionDisplayStatus(auction, item),
              theme,
            };
            if (getItemHref) {
              context.href = getItemHref(auction, item);
            }
            if (cardStyle) {
              context.style = cardStyle;
            }
            const { onSelect, ...cardContext } = context;
            return (
              <li key={item.id}>
                {renderItem ? (
                  renderItem(context)
                ) : (
                  <LockerverseAuctionItemCard
                    {...cardContext}
                    {...(onSelectItem ? { onSelect } : {})}
                    autoRotateImages={autoRotateImages}
                  />
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="lockerverse-auctions__catalog-empty">
          No items match this auction view.
        </p>
      )}
    </>
  );
}
